import { createForm } from "@tanstack/solid-form";
import { createMemo, onMount } from "solid-js";
import { produce } from "solid-js/store";
import type { z } from "zod";
import { useI18n } from "@/api/i18n";
import { TagInput, TagInputSuggestions } from "@/components/shared/input";
import { Button } from "@/components/ui/button";
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  FieldContainer,
  FieldContainerLabel,
  FieldInfo,
} from "@/components/ui/input";
import { categoryLabel } from "@/domains/freelance/taxonomy/store/taxonomy-store";
import {
  boundUnknownSkillTerms,
  MAX_SKILL_TERM_LENGTH,
  MAX_SKILL_TERMS,
} from "@/libs/skill-input";
import { useOnboardingSkillChipsFor } from "../../api/skill-chips";
import {
  linkedinOnboardingValidationSchema,
  useOnboardingStore,
} from "../../context/OnboardingContext";

// The skills only: the sector has its own card and modal in the hub
// (freelancer-categories FE14, AC23).
const professionalValidationSchema = linkedinOnboardingValidationSchema.pick({
  skills: true,
});

type ProfessionalFormValues = z.infer<typeof professionalValidationSchema>;

interface ProfessionalModalProps {
  onClose: () => void;
}

/** The hub's «Competenze» modal (AC23): the skills, with chips for both sectors. */
export const ProfessionalModal = (props: ProfessionalModalProps) => {
  const [store, setStore] = useOnboardingStore();
  const { t } = useI18n();

  // Clean skills from store on mount to handle corrupted data from persistence
  onMount(() => {
    const cleanedSkills = boundUnknownSkillTerms(store.skills.value);
    if (JSON.stringify(cleanedSkills) !== JSON.stringify(store.skills.value)) {
      setStore(
        produce((draft) => {
          draft.skills.value = cleanedSkills;
        }),
      );
    }
  });

  const form = createForm(() => ({
    defaultValues: {
      skills: boundUnknownSkillTerms(store.skills.value),
    } as ProfessionalFormValues,
    onSubmit: async ({ value }) => {
      const validatedData = professionalValidationSchema.safeParse(value);
      if (!validatedData.success) {
        console.error("Validation error", validatedData.error);
        // Update store with errors
        setStore(
          produce((draft) => {
            const errors = validatedData.error.flatten().fieldErrors;
            draft.skills.error = errors.skills?.[0] ?? null;
          }),
        );
        return;
      }

      // Update store with validated data
      setStore(
        produce((draft) => {
          draft.skills.value = validatedData.data.skills;
          draft.skills.error = null;
        }),
      );

      props.onClose();
    },
  }));

  // The sectors saved in the hub's «Settore» card: the secondary counts only
  // if it is there and differs from the primary. Same chips as `steps/Skills`
  // (D11, AC18), the primary first.
  const sectors = createMemo((): (string | null)[] => {
    const primary = store.sector.value.job_category;
    const secondary = store.sector.value.job_category_secondary;
    return [primary, secondary && secondary !== primary ? secondary : null];
  });
  const suggestions = useOnboardingSkillChipsFor(sectors);

  // «Suggerite per X» / «Suggerite per X e Y», only with chips to show.
  const suggestionsLabel = createMemo(() => {
    if (suggestions().length === 0) return undefined;
    const labels = sectors()
      .filter((key): key is string => !!key)
      .map(categoryLabel);
    if (labels.length === 0) return undefined;
    return t("onboarding.steps.skills.suggestedFor", {
      labels: labels.join(" e "),
    });
  });

  return (
    <div class="flex flex-col gap-6">
      <DialogHeader>
        <DialogTitle>{t("onboarding.steps.skills.title")}</DialogTitle>
        <DialogDescription>
          {t("onboarding.steps.skills.description")}
        </DialogDescription>
      </DialogHeader>

      <div class="flex flex-col gap-6">
        <form.Field
          name="skills"
          validators={{
            onChange: professionalValidationSchema.shape.skills,
          }}
        >
          {(field) => (
            <FieldContainer>
              <FieldContainerLabel for={field().name}>
                {t("onboarding.steps.skills.yourSkills")}
              </FieldContainerLabel>
              <TagInput
                fieldApi={field()}
                class="bg-white"
                maxTags={MAX_SKILL_TERMS}
                maxTagLength={MAX_SKILL_TERM_LENGTH}
              />
              <TagInputSuggestions
                fieldApi={field()}
                suggestions={suggestions()}
                label={suggestionsLabel()}
                maxTags={MAX_SKILL_TERMS}
                maxTagLength={MAX_SKILL_TERM_LENGTH}
              />
              <FieldInfo field={field()} />
            </FieldContainer>
          )}
        </form.Field>
      </div>

      <div class="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={props.onClose}>
          {t("common.cancel")}
        </Button>
        <form.Subscribe
          selector={(state) => ({
            canSubmit: state.canSubmit,
            isSubmitting: state.isSubmitting,
            isValid: state.isValid,
            isSubmittable: professionalValidationSchema.safeParse(state.values)
              .success,
          })}
        >
          {(state) => (
            <Button
              onClick={form.handleSubmit}
              disabled={
                !state().isValid ||
                state().isSubmitting ||
                !state().isSubmittable
              }
            >
              {state().isSubmitting ? t("common.saving") : t("common.save")}
            </Button>
          )}
        </form.Subscribe>
      </div>
    </div>
  );
};
